import React, { useState } from "react";
import {
  ArrowLeft,
  Image as ImageIcon,
  X,
  ChevronDown,
  Plus,
  Trash2,
} from "lucide-react";
import { useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  handleDrag,
  removeImage,
  handleFiles,
} from "../../utils/imageUpload";
import { useCategories } from "../../hooks/useCategories";
import { createProduct } from "../../services/productService";
import { getErrorMessage } from "../../utils/getErrorMessage";

const AddProduct = () => {
  const [status, setStatus] = useState("active");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [images, setImages] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [variations, setVariations] = useState([]);
  const { categories, loading: categoriesLoading } = useCategories();
  const [bestSeller, setBestSeller] = useState("0");
  const [formData, setFormData] = useState({
    title: "",
    product_category_id: "",
    price: "",
    gender: "",
    quantity: "",
    description: "",
    discount: "",
  });

  const addVariation = () => {
    setVariations([...variations, { id: Date.now(), name: "", values: [] }]);
  };

  const updateVariationName = (id, newName) => {
    setVariations(
      variations.map((v) => (v.id === id ? { ...v, name: newName } : v)),
    );
  };

  const addVariationValue = (variationId) => {
    setVariations(
      variations.map((v) =>
        v.id === variationId ? { ...v, values: [...v.values, ""] } : v,
      ),
    );
  };

  const updateVariationValue = (variationId, valueIndex, newValue) => {
    setVariations(
      variations.map((v) =>
        v.id === variationId
          ? {
              ...v,
              values: v.values.map((val, idx) =>
                idx === valueIndex ? newValue : val,
              ),
            }
          : v,
      ),
    );
  };

  const removeVariationValue = (variationId, valueIndex) => {
    setVariations(
      variations.map((v) =>
        v.id === variationId
          ? { ...v, values: v.values.filter((_, idx) => idx !== valueIndex) }
          : v,
      ),
    );
  };

  const removeVariation = (id) => {
    setVariations(variations.filter((v) => v.id !== id));
  };

  const { mutate: handleCreateProduct, isPending } = useMutation({
    mutationFn: async () => {
      const payload = new FormData();
      payload.append("title", formData.title);
      payload.append("product_category_id", formData.product_category_id);
      payload.append("price", formData.price);
      payload.append("gender", formData.gender);
      payload.append("quantity", formData.quantity);
      payload.append("description", formData.description);
      payload.append("best_seller", bestSeller);
      payload.append("active_status", status);
      if (formData.discount) payload.append("discount", formData.discount);

      // Add images
      images.forEach((image, index) => {
        // Handle both File objects and base64 strings
        if (image instanceof File) {
          payload.append(`images[]`, image);
        } else if (image.startsWith("data:")) {
          // Convert base64 to blob for base64 images
          const arr = image.split(",");
          const mime = arr[0].match(/:(.*?);/)[1];
          const bstr = atob(arr[1]);
          const n = bstr.length;
          const u8arr = new Uint8Array(n);
          for (let i = 0; i < n; i++) {
            u8arr[i] = bstr.charCodeAt(i);
          }
          const blob = new Blob([u8arr], { type: mime });
          payload.append(`images[]`, blob, `image-${index}.jpg`);
        }
      });

      // Add variations - only include name and values, remove the id
      variations.forEach((variation, index) => {
        payload.append(`variations[${index}][name]`, variation.name);
        variation.values.forEach((value, valueIndex) => {
          payload.append(`variations[${index}][values][${valueIndex}]`, value);
        });
      });

      return createProduct(payload);
    },
    onSuccess: () => {
      toast.success("Product created successfully!");
      queryClient.invalidateQueries({ queryKey: ["products"] });
      navigate("/product");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to create product."));
    },
  });

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error("Product name is required");
      return;
    }
    if (formData.title.length > 255) {
      toast.error("Product name must be 255 characters or less");
      return;
    }
    if (!formData.description.trim()) {
      toast.error("Product description is required");
      return;
    }
    if (formData.description.length > 2000) {
      toast.error("Description must be 2000 characters or less");
      return;
    }
    if (!formData.product_category_id) {
      toast.error("Please select a category");
      return;
    }
    if (!formData.price) {
      toast.error("Price is required");
      return;
    }
    if (!formData.gender) {
      toast.error("Please select a gender");
      return;
    }
    if (!formData.quantity) {
      toast.error("Quantity is required");
      return;
    }
    if (!images || images.length === 0) {
      toast.error("Please add at least one product image");
      return;
    }
    handleCreateProduct();
  };

  return (
    <div className="min-h-screen bg-white p-4 md:p-8">
      {/* --- HEADER --- */}
      <div className="mx-auto mb-6 border-b border-[#EEF1F3] pb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-800 hover:text-black transition-colors mb-2 active:scale-95 duration-100"
        >
          <ArrowLeft size={20} />
          <h1 className="text-xl font-medium text-[#0C0C0E]">
            Add New Product
          </h1>
        </button>
        <p className="font-medium text-sm text-[#7D828D]">
          Fill in the details to create a new product
        </p>
      </div>

      <div className="mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* --- LEFT COLUMN --- */}
        <div className="lg:col-span-2 space-y-8">
          {/* Basic Information */}
          <section>
            <h2 className="text-base font-medium text-[#0C0C0E] mb-4">
              Basic Information
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5 text-[#383B41]">
                  Product Name *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  placeholder="Enter product name"
                  className="w-full p-2.5 bg-white border border-[#DFDFDF] rounded-lg focus:ring-2 focus:ring-[#D9CBBF] outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5 text-[#383B41]">
                  SKU *
                </label>
                <input
                  type="text"
                  placeholder="eg. WH-001"
                  className="w-full p-2.5 bg-white border border-[#DFDFDF] rounded-lg focus:ring-2 focus:ring-[#D9CBBF] outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5 text-[#383B41]">
                  Description
                </label>
                <div className="border border-[#DFDFDF] rounded-lg overflow-hidden bg-white">
                  <textarea
                    rows="6"
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    placeholder="Enter product description"
                    className="w-full p-4 resize-none outline-none text-sm bg-slate-50/30"
                  ></textarea>
                </div>
              </div>
            </div>
          </section>

          {/* Product Images */}
          <section>
            <h2 className="text-base font-medium text-[#0C0C0E] mb-4">
              Product Images
            </h2>
            <input
              type="file"
              id="imageInput"
              multiple
              accept="image/png, image/jpeg"
              onChange={(e) => handleFiles(e.target.files, setImages)}
              className="hidden"
            />
            <div
              onDragEnter={(e) => handleDrag(e, setDragActive)}
              onDragLeave={(e) => handleDrag(e, setDragActive)}
              onDragOver={(e) => handleDrag(e, setDragActive)}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragActive(false);
                if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                  handleFiles(e.dataTransfer.files, setImages);
                }
              }}
              onClick={() => document.getElementById("imageInput").click()}
              className={`bg-white border-2 rounded-xl p-8 flex flex-col items-center justify-center mb-4 cursor-pointer transition-colors ${dragActive ? "border-[#D9CBBF] bg-orange-50" : "border-[#E4E7EC]"}`}
            >
              <div className="w-12 h-12 bg-orange-50 rounded-full flex items-center justify-center mb-3">
                <ImageIcon className="text-orange-200" size={24} />
              </div>
              <p className="text-base text-[#0C0C0E]">
                Drag & drop files here or{" "}
                <span className="text-[#BB6979]">choose files</span>
              </p>
              <p className="text-sm text-[#7D828D] mt-2 max-w-80 text-center">
                PNG & JPG image formats accepted • Recommended size: 908 x 652
                px • Max 6 images
              </p>
            </div>

            {/* Image Preview Grid */}
            {images.length > 0 && (
              <div className="grid grid-cols-4 gap-4">
                {images.map((img, idx) => (
                  <div
                    key={idx}
                    className="max-w-36 max-h-36 relative group aspect-square rounded-lg border border-[#DFDFDF] bg-white p-1"
                  >
                    <img
                      src={img}
                      alt="Product"
                      className="w-full h-full object-cover rounded-md"
                    />
                    <button
                      onClick={() => removeImage(idx, setImages)}
                      className="absolute -top-1 -right-1 bg-rose-500 text-white rounded-full p-0.5 border-2 border-white hover:bg-rose-600 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Product Variations */}
          <section>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base font-medium text-[#0C0C0E]">
                Product Variations
              </h2>
              <button
                onClick={addVariation}
                className="flex items-center gap-1 bg-[#D9CBBF] hover:bg-[#c9b9ad] text-black text-sm px-3 py-1.5 rounded-lg font-medium transition-colors active:scale-95 duration-100"
              >
                <Plus size={16} /> Add Variation
              </button>
            </div>

            {variations.length === 0 ? (
              <p className="text-sm text-[#7D828D]">
                Add variations like Size, Color, or Material to offer different
                options of this product. Each variation can have multiple
                values.
              </p>
            ) : (
              <div className="space-y-6">
                {variations.map((variation) => (
                  <div
                    key={variation.id}
                    className="border border-[#DFDFDF] rounded-lg p-4 bg-slate-50/30"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium mb-1.5 text-[#383B41]">
                          Variation Name (e.g., Size, Color)
                        </label>
                        <input
                          type="text"
                          value={variation.name}
                          onChange={(e) =>
                            updateVariationName(variation.id, e.target.value)
                          }
                          placeholder="Enter variation name"
                          className="w-full p-2.5 bg-white border border-[#DFDFDF] rounded-lg focus:ring-2 focus:ring-[#D9CBBF] outline-none"
                        />
                      </div>
                      <button
                        onClick={() => removeVariation(variation.id)}
                        className="text-red-500 hover:text-red-600 transition-colors ml-2 mt-6"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>

                    <div className="mb-3">
                      <label className="block text-sm font-medium mb-2 text-[#383B41]">
                        Values
                      </label>
                      <div className="space-y-2">
                        {variation.values.map((value, idx) => (
                          <div key={idx} className="flex gap-2">
                            <input
                              type="text"
                              value={value}
                              onChange={(e) =>
                                updateVariationValue(
                                  variation.id,
                                  idx,
                                  e.target.value,
                                )
                              }
                              placeholder={`Value ${idx + 1}`}
                              className="flex-1 p-2.5 bg-white border border-[#DFDFDF] rounded-lg focus:ring-2 focus:ring-[#D9CBBF] outline-none text-sm"
                            />
                            <button
                              onClick={() =>
                                removeVariationValue(variation.id, idx)
                              }
                              className="text-red-500 hover:text-red-600 transition-colors p-2"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <button
                      onClick={() => addVariationValue(variation.id)}
                      className="flex items-center gap-1 text-[#556B2F] hover:text-[#6B7143] font-medium text-sm transition-colors"
                    >
                      <Plus size={14} /> Add Value
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
          <section>
            <h2 className="text-base font-bold text-[#0C0C0E] mb-4">Pricing</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5 text-[#383B41]">
                  Price *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                    ₦
                  </span>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleFormChange}
                    placeholder="0.00"
                    className="w-full pl-8 pr-4 py-2.5 font-semibold bg-white border border-[#DFDFDF] rounded-lg outline-none focus:ring-2 focus:ring-[#D9CBBF]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5 text-[#383B41]">
                  Discount{" "}
                  <span className="text-sm text-slate-400">
                    (Price that will appear slashed)
                  </span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                    ₦
                  </span>
                  <input
                    type="number"
                    name="discount"
                    value={formData.discount}
                    onChange={handleFormChange}
                    placeholder="0.00"
                    className="w-full pl-8 pr-4 py-2.5 font-semibold bg-white border border-[#DFDFDF] rounded-lg outline-none focus:ring-2 focus:ring-[#D9CBBF]"
                  />
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* --- RIGHT COLUMN --- */}
        <div className="space-y-8">
          {/* Organization */}
          <section>
            <h2 className="text-base font-medium text-[#0C0C0E] mb-4">
              Organization
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5 text-[#383B41]">
                  Category *
                </label>
                <div className="relative">
                  <select
                    name="product_category_id"
                    value={formData.product_category_id}
                    onChange={handleFormChange}
                    className="w-full p-2.5 bg-white border border-[#DFDFDF] rounded-lg appearance-none outline-none focus:ring-2 focus:ring-[#D9CBBF]"
                    disabled={categoriesLoading}
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                    size={18}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5 text-[#383B41]">
                  Collection (Gender) *
                </label>
                <div className="relative">
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleFormChange}
                    className="w-full p-2.5 bg-white border border-[#DFDFDF] rounded-lg appearance-none outline-none focus:ring-2 focus:ring-[#D9CBBF]"
                  >
                    <option value="">Select Collection</option>
                    <option value="male">Men</option>
                    <option value="female">Women</option>
                    <option value="kids">Kids</option>
                    <option value="unisex">Unisex</option>
                  </select>
                  <ChevronDown
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                    size={18}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5 text-[#383B41]">
                  Stock Quantity *
                </label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleFormChange}
                  placeholder="0"
                  className="w-full p-2.5 bg-white border border-[#DFDFDF] rounded-lg outline-none focus:ring-2 focus:ring-[#D9CBBF]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5 text-[#383B41]">
                  Set Best Seller
                </label>
                <div className="flex gap-2 bg-white border border-[#DFDFDF] rounded-lg p-1">
                  <label className="flex-1 flex items-center justify-center gap-2 cursor-pointer py-2 px-3 rounded-md transition-colors hover:bg-slate-50">
                    <input
                      type="radio"
                      name="bestSeller"
                      value="1"
                      checked={bestSeller === "1"}
                      onChange={(e) => setBestSeller(e.target.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium text-[#383B41]">
                      Yes
                    </span>
                  </label>
                  <label className="flex-1 flex items-center justify-center gap-2 cursor-pointer py-2 px-3 rounded-md transition-colors hover:bg-slate-50">
                    <input
                      type="radio"
                      name="bestSeller"
                      value="0"
                      checked={bestSeller === "0"}
                      onChange={(e) => setBestSeller(e.target.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium text-[#383B41]">
                      No
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </section>

          {/* Status */}
          <section>
            <h2 className="text-base font-bold text-[#0C0C0E] mb-4">Status</h2>
            <div className="space-y-4">
              {/* Active Option */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="mt-1 relative flex items-center justify-center">
                  <input
                    type="radio"
                    name="status"
                    checked={status === "active"}
                    onChange={() => setStatus("active")}
                    className="sr-only"
                  />
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${status === "active" ? "border-rose-400" : "border-slate-300"}`}
                  >
                    {status === "active" && (
                      <div className="w-2.5 h-2.5 rounded-full bg-[#D88797]" />
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-[#0C0C0E]">Active</p>
                  <p className="text-sm text-[#7D828D]">
                    Product is visible to customers
                  </p>
                </div>
              </label>

              {/* Inactive Option */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="mt-1 relative flex items-center justify-center">
                  <input
                    type="radio"
                    name="status"
                    checked={status === "inactive"}
                    onChange={() => setStatus("inactive")}
                    className="sr-only"
                  />
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${status === "inactive" ? "border-rose-400" : "border-slate-300"}`}
                  >
                    {status === "inactive" && (
                      <div className="w-2.5 h-2.5 rounded-full bg-rose-400" />
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-[#0C0C0E]">Inactive</p>
                  <p className="text-sm text-[#7D828D]">
                    Product is hidden from customers
                  </p>
                </div>
              </label>
            </div>
          </section>

          {/* Action Buttons */}
          <div className="pt-4 space-y-3 text-sm">
            <button
              onClick={handleSubmit}
              disabled={isPending}
              className="w-full text-[#1C1A19] bg-[#D9CBBF] hover:bg-[#c9b9ad] disabled:opacity-50 disabled:cursor-not-allowed font-medium py-3 rounded-lg transition-colors active:scale-95 active:shadow-sm duration-100"
            >
              {isPending ? "Creating..." : "Create Product"}
            </button>
            <button
              onClick={() => navigate(-1)}
              className="w-full bg-white border border-[#DFDFDF] text-[#0C0C0E] font-medium py-3 rounded-lg hover:bg-slate-50 transition-colors active:scale-95 active:shadow-sm duration-100"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddProduct;
